/* Tiny drawings of each chart variant for the chart-type gallery. Two
 * colours (a = main series, b = second series) so stacked / 100% / combo
 * are recognisable at a glance -- a generic icon can't show the difference. */

const A = "var(--st-g1, #0f6cbd)";
const B = "var(--st-g2, #18a8c9)";
const C = "var(--st-g3, #9fc3e6)";

function Axis() {
  return <path d="M5 4 V31 H43" fill="none" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.2" />;
}

export function VariantGlyph({ id, size = 48 }: { id: string; size?: number }) {
  const h = Math.round((size * 34) / 48);
  const body = (() => {
    switch (id) {
      case "column":
        return (
          <>
            <Axis />
            <rect x="9" y="17" width="5" height="14" fill={A} rx="1" />
            <rect x="15" y="12" width="5" height="19" fill={B} rx="1" />
            <rect x="24" y="9" width="5" height="22" fill={A} rx="1" />
            <rect x="30" y="15" width="5" height="16" fill={B} rx="1" />
          </>
        );
      case "column-stacked":
        return (
          <>
            <Axis />
            {[[9, 12], [18, 7], [27, 15], [36, 10]].map(([x, top]) => (
              <g key={x}>
                <rect x={x} y={top} width="6" height={(31 - top) * 0.45} fill={B} />
                <rect x={x} y={top + (31 - top) * 0.45} width="6" height={(31 - top) * 0.55} fill={A} />
              </g>
            ))}
          </>
        );
      case "column-100":
        return (
          <>
            <Axis />
            {[[9, 0.35], [18, 0.55], [27, 0.3], [36, 0.6]].map(([x, f]) => (
              <g key={x}>
                <rect x={x} y="6" width="6" height={25 * f} fill={B} />
                <rect x={x} y={6 + 25 * f} width="6" height={25 * (1 - f)} fill={A} />
              </g>
            ))}
          </>
        );
      case "bar":
        return (
          <>
            <Axis />
            <rect x="5" y="7" width="30" height="5" fill={A} rx="1" />
            <rect x="5" y="14" width="22" height="5" fill={B} rx="1" />
            <rect x="5" y="21" width="15" height="5" fill={A} rx="1" />
          </>
        );
      case "bar-stacked":
        return (
          <>
            <Axis />
            {[[7, 34], [15, 26], [23, 18]].map(([y, w]) => (
              <g key={y}>
                <rect x="5" y={y} width={w * 0.6} height="6" fill={A} />
                <rect x={5 + w * 0.6} y={y} width={w * 0.4} height="6" fill={B} />
              </g>
            ))}
          </>
        );
      case "bar-100":
        return (
          <>
            <Axis />
            {[[7, 0.6], [15, 0.4], [23, 0.7]].map(([y, f]) => (
              <g key={y}>
                <rect x="5" y={y} width={36 * f} height="6" fill={A} />
                <rect x={5 + 36 * f} y={y} width={36 * (1 - f)} height="6" fill={B} />
              </g>
            ))}
          </>
        );
      case "line":
        return (
          <>
            <Axis />
            <polyline points="7,25 15,18 22,21 30,11 40,14" fill="none" stroke={A} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
            <polyline points="7,28 15,26 22,24 30,22 40,19" fill="none" stroke={B} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
          </>
        );
      case "area":
        return (
          <>
            <Axis />
            <path d="M6 31 L6 22 L15 15 L23 19 L31 9 L42 13 L42 31 Z" fill={A} fillOpacity="0.35" />
            <polyline points="6,22 15,15 23,19 31,9 42,13" fill="none" stroke={A} strokeWidth="2" strokeLinejoin="round" />
          </>
        );
      case "area-stacked":
        return (
          <>
            <Axis />
            <path d="M6 31 L6 16 L15 11 L23 14 L31 6 L42 9 L42 31 Z" fill={B} fillOpacity="0.55" />
            <path d="M6 31 L6 24 L15 21 L23 23 L31 17 L42 20 L42 31 Z" fill={A} fillOpacity="0.8" />
          </>
        );
      case "area-100":
        return (
          <>
            <Axis />
            <path d="M5 6 H43 V31 H5 Z" fill={C} />
            <path d="M5 18 L15 14 L25 20 L35 12 L43 16 V31 H5 Z" fill={A} />
          </>
        );
      case "bullet":
        return (
          <>
            {[8, 17, 26].map((y, i) => (
              <g key={y}>
                <rect x="6" y={y} width="36" height="6" fill={C} rx="1" />
                <rect x="6" y={y + 1.8} width={[26, 18, 31][i]} height="2.4" fill={A} />
                <rect x={[30, 27, 34][i]} y={y - 0.5} width="1.6" height="7" fill="currentColor" />
              </g>
            ))}
          </>
        );
      case "scatter":
        return (
          <>
            <Axis />
            {[[11, 25, 2], [16, 20, 3], [21, 22, 2], [26, 15, 4], [32, 12, 2.5], [38, 8, 3.5]].map(([x, y, r]) => (
              <circle key={`${x}`} cx={x} cy={y} r={r} fill={A} fillOpacity="0.75" />
            ))}
            <path d="M8 27 L41 7" stroke={B} strokeWidth="1.2" strokeDasharray="3 2" />
          </>
        );
      case "heatmap":
        return (
          <>
            {[0, 1, 2, 3].map((r) => [0, 1, 2, 3, 4].map((c) => (
              <rect key={`${r}-${c}`} x={6 + c * 7.4} y={5 + r * 6.6} width="6.6" height="5.8" rx="1" fill={A} fillOpacity={[0.2, 0.5, 0.85, 0.35, 0.65][(r + c) % 5]} />
            )))}
          </>
        );
      case "kpi-trend":
        return (
          <>
            <rect x="6" y="6" width="20" height="7" rx="1.5" fill="currentColor" fillOpacity="0.75" />
            <path d="M28 9 l2.5 -3 l2.5 3 z" fill={B} />
            <path d="M6 28 L14 24 L21 26 L29 18 L36 20 L43 13 V31 H6 Z" fill={A} fillOpacity="0.2" />
            <path d="M6 28 L14 24 L21 26 L29 18 L36 20 L43 13" fill="none" stroke={A} strokeWidth="1.8" />
          </>
        );
      case "combo":
        return (
          <>
            <Axis />
            <rect x="9" y="17" width="6" height="14" fill={C} />
            <rect x="18" y="12" width="6" height="19" fill={C} />
            <rect x="27" y="19" width="6" height="12" fill={C} />
            <rect x="36" y="10" width="6" height="21" fill={C} />
            <polyline points="12,12 21,8 30,13 39,5" fill="none" stroke={A} strokeWidth="2" strokeLinejoin="round" />
            {[[12, 12], [21, 8], [30, 13], [39, 5]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="1.8" fill={A} />)}
          </>
        );
      case "waterfall":
        return (
          <>
            <Axis />
            <rect x="8" y="16" width="6" height="15" fill={A} />
            <rect x="16" y="9" width="6" height="7" fill="#22c55e" />
            <rect x="24" y="9" width="6" height="6" fill="#ef4444" />
            <rect x="32" y="15" width="6" height="16" fill={A} />
          </>
        );
      case "donut":
        return (
          <>
            <circle cx="24" cy="17" r="11" fill="none" stroke={C} strokeWidth="6" />
            <circle cx="24" cy="17" r="11" fill="none" stroke={A} strokeWidth="6" strokeDasharray="30 100" transform="rotate(-90 24 17)" />
            <circle cx="24" cy="17" r="11" fill="none" stroke={B} strokeWidth="6" strokeDasharray="18 100" strokeDashoffset="-30" transform="rotate(-90 24 17)" />
          </>
        );
      case "pie":
        return (
          <>
            <circle cx="24" cy="17" r="13" fill={C} />
            <path d="M24 17 L24 4 A13 13 0 0 1 36.4 21 Z" fill={A} />
            <path d="M24 17 L36.4 21 A13 13 0 0 1 20 29.4 Z" fill={B} />
          </>
        );
      case "treemap":
        return (
          <>
            <rect x="5" y="4" width="21" height="27" fill={A} rx="1" />
            <rect x="27" y="4" width="16" height="15" fill={B} rx="1" />
            <rect x="27" y="20" width="9" height="11" fill={C} rx="1" />
            <rect x="37" y="20" width="6" height="11" fill={B} fillOpacity="0.6" rx="1" />
          </>
        );
      case "funnel":
        return (
          <>
            <rect x="6" y="4" width="36" height="6" fill={A} rx="1" />
            <rect x="11" y="12" width="26" height="6" fill={B} rx="1" />
            <rect x="16" y="20" width="16" height="5" fill={A} fillOpacity="0.75" rx="1" />
            <rect x="20" y="27" width="8" height="4" fill={B} fillOpacity="0.75" rx="1" />
          </>
        );
      case "kpi":
        return (
          <>
            <rect x="5" y="4" width="38" height="27" rx="3" fill="none" stroke="currentColor" strokeOpacity="0.35" />
            <rect x="10" y="9" width="14" height="3" rx="1" fill="currentColor" fillOpacity="0.4" />
            <text x="10" y="25" fontSize="12" fontWeight="800" fill={A} fontFamily="system-ui, sans-serif">42K</text>
          </>
        );
      case "gauge":
        return (
          <>
            <path d="M8 28 A16 16 0 0 1 40 28" fill="none" stroke={C} strokeWidth="5" strokeLinecap="round" />
            <path d="M8 28 A16 16 0 0 1 32 14.2" fill="none" stroke={A} strokeWidth="5" strokeLinecap="round" />
            <line x1="24" y1="28" x2="31" y2="17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="24" cy="28" r="2" fill="currentColor" />
          </>
        );
      default:
        return <Axis />;
    }
  })();
  return (
    <svg width={size} height={h} viewBox="0 0 48 34" aria-hidden="true" focusable="false">
      {body}
    </svg>
  );
}
